import { categoryIcon } from "./icon-map";
import { Badge } from "./Badge";
import { RedirectNotice } from "./RedirectNotice";
import Link from "@/components/ui/RouterLink";
import type { Product } from "@/data/products";
import { cn } from "@/lib/utils";

export function ProductCard({ product, detailsHref }: { product: Product; detailsHref?: string }) {
  const Icon = categoryIcon[product.category];

  return (
    <div
      className={cn(
        "relative flex h-full flex-col rounded-card border bg-navy-card p-6 transition-all duration-200 hover:-translate-y-1",
        product.featured
          ? "border-cyan/50 shadow-[0_0_0_1px_rgba(37,199,255,0.25),0_20px_40px_-20px_rgba(37,199,255,0.35)]"
          : "border-border hover:border-cyan/40"
      )}
    >
      {product.featured && (
        <span className="absolute -top-3 left-6 rounded-full bg-gradient-brand px-3 py-1 text-[11px] font-medium text-white">
          Most popular
        </span>
      )}

      <div className="flex items-center justify-between">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-navy-secondary text-cyan">
          <Icon className="h-5 w-5" strokeWidth={1.6} />
        </div>
        {!product.available && <Badge tone="warning">Coming soon</Badge>}
      </div>

      <h3 className="mt-4 font-display text-lg font-semibold text-text-primary">
        {product.name}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-text-secondary">{product.description}</p>

      <ul className="mt-4 space-y-2 border-t border-border pt-4">
        {product.specifications.slice(0, 4).map((spec) => (
          <li key={spec.label} className="flex items-center justify-between text-xs">
            <span className="text-text-muted">{spec.label}</span>
            <span className="font-medium text-text-secondary">{spec.value}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6 flex items-baseline gap-1">
        {product.price ? (
          <>
            <span className="text-xs text-text-muted">Starting at</span>
            <span className="font-display text-xl font-semibold text-text-primary">
              {product.price}
            </span>
            {product.billingCycle && (
              <span className="text-xs text-text-muted">/{product.billingCycle}</span>
            )}
          </>
        ) : (
          <span className="text-xs text-text-muted">Pricing shown in the billing portal</span>
        )}
      </div>

      <div className="mt-5 flex flex-col gap-2">
        {product.available ? (
          <RedirectNotice
            href={product.buyNowUrl}
            eventPayload={{ product: product.id }}
            className="focus-ring w-full rounded-full bg-gradient-brand px-4 py-2.5 text-center text-sm font-medium text-white transition hover:brightness-110"
          >
            Configure
          </RedirectNotice>
        ) : (
          <Link
            href="/contact"
            className="focus-ring w-full rounded-full border border-border bg-navy-secondary px-4 py-2.5 text-center text-sm font-medium text-text-primary transition hover:border-cyan/50"
          >
            Contact Us
          </Link>
        )}
        {detailsHref && (
          <Link
            href={detailsHref}
            className="focus-ring text-center text-xs font-medium text-text-muted transition hover:text-cyan"
          >
            View details
          </Link>
        )}
      </div>
    </div>
  );
}
